/**
 * ApiContextDominios
 * ==================
 *
 * Contexto React que centraliza TODAS las llamadas HTTP contra los controladores
 * del backend organizados por dominio (`server/domains/*`). Funciona como la
 * contraparte frontend de la arquitectura por dominios definida en
 * `server/domains/ARCHITECTURE.md`.
 *
 * ¿Por qué existe?
 * ----------------
 * - Hay un `ErpContext` histórico que concentra lógica legacy (controllers
 *   planos en `server/controllers/*`). A medida que se migran endpoints al
 *   modelo de dominios (transaccionDomain, adminDomain, itemDomain, etc.),
 *   las llamadas correspondientes se dan de alta en este archivo. Esto evita
 *   inflar `ErpContext` y mantiene separada la superficie "nueva" de la vieja.
 * - Concentra la construcción de URLs (`serverURL`), el manejo uniforme de
 *   errores (cada función devuelve `{ ok, data | message }` o un fallback
 *   seguro) y el parseo básico de respuestas, para que los componentes no
 *   tengan que repetir `axios.get(...).catch(...)` en todos lados.
 * - Expone estado compartido asociado a catálogos que se piden una única vez
 *   (tipos de medio de pago, ubicaciones, etc.) y se consumen desde múltiples
 *   pantallas sin refetch redundante.
 *
 * Convenciones
 * ------------
 * - Cada función nueva se registra en el `value` del Provider al final del
 *   archivo. Si no se registra, el componente que la consuma va a explotar
 *   con "x is not a function" en el bundle minificado.
 * - El tenant y el usuario viajan por cookies HTTP (seteadas en login) — no
 *   hace falta pasarlos como parámetros.
 * - Para endpoints de escritura, devolver `{ ok, data }` en éxito y
 *   `{ ok: false, message }` en error, para que el componente muestre el
 *   mensaje del backend sin tener que leer `error.response.data`.
 */

import React, { createContext, useState } from "react"
import axios from "axios"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import tz from "dayjs/plugin/timezone"
import { abrirFacturaHtml } from "./facturaAfipHtmlGenerator.js"

dayjs.extend(utc)
dayjs.extend(tz)

// URL GLOBAL - Desde celular usa la misma IP (ej. 192.168.100.30:5000)
function getServerURL() {
  if (typeof window !== "undefined" && window.location.hostname !== "localhost") {
    const protocol = window.location.protocol === "https:" ? "https" : "http"
    return `${protocol}://${window.location.hostname}:5000`
  }
  const MODE = process.env.REACT_APP_MODE
  if (MODE === "production") return process.env.REACT_APP_URL_PROD || "http://localhost:5000"
  return process.env.REACT_APP_URL_DEV || "http://localhost:5000"
}
const serverURL = getServerURL()

export const ApiContextDominios = createContext()

export const ApiDominiosProvider = ({ children }) => {
  // STATE BASE (reutilizable por dominios)
  const [itemTresLetras, setItemTresLetras] = useState([])
  const [entidadesClientesTresLetras, setEntidadesClientesTresLetras] = useState([])
  const [ubicaciones, setUbicaciones] = useState([])
  const [negocios, setNegocios] = useState([])
  const [tipoEntidades, setTipoEntidades] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [tipoMedioDePago, setTipoMedioDePago] = useState([])
  const [medioDePagoIdTipoEspecifico, setMedioDePagoIdTipoEspecifico] = useState([])
  const [impuestos, setImpuestos] = useState([])
  const [arrayImpuestos, setArrayImpuestos] = useState([])
  const [tiposFacturas, setTiposFacturas] = useState([])
  const [cuentasCorrientes, setCuentasCorrientes] = useState([])
  const [itemAtributoNoEliminados, setItemAtributoNoEliminados] = useState([])

  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // API: ITEM (itemController en su dominio)
  const getItemTresLetras = async (searchText) => {
    try {
      const response = await axios.get(
        `${serverURL}/itemDomain/getItemTresLetras?search=${searchText}`,
      )
      setItemTresLetras(response.data.items)
      return {
        items: response.data.items,
        buscadoPorScanner: response.data.buscadoPorScanner,
        buscadoPorBalanza: response.data.buscadoPorBalanza,
        pesoBalanza: response.data.pesoBalanza,
      }
    } catch (error) {
      console.error("Error fetching item:", error)
      return {
        items: [],
        buscadoPorScanner: false,
        buscadoPorBalanza: false,
        pesoBalanza: null,
      }
    }
  }

  const clearItemTresLetras = () => {
    setItemTresLetras([])
  }

  const getItemById = async (id) => {
    try {
      const response = await axios.get(`${serverURL}/itemDomain/getItemById/${id}`)
      return response.data
    } catch (error) {
      console.error("Error fetching item by id:", error)
      return null
    }
  }

  const getItemUbicacionFilterByUbicacion = async (itemID, ubicacionID) => {
    try {
      const response = await axios.get(
        `${serverURL}/itemDomain/getItemUbicacionFilterByUbicacion/${itemID}/${ubicacionID}`
      )
      return response.data
    } catch (error) {
      console.error("Error fetching item ubicacion:", error)
      return []
    }
  }

  const getItemAtributoNoEliminados = async () => {
    try {
      const response = await axios.get(`${serverURL}/itemDomain/getItemAtributoNoEliminados`)
      setItemAtributoNoEliminados(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching item atributos:", error)
      return []
    }
  }

  const updateItemUbicacion = async (idItem, idUbicacion, inventarioItem, proviene) => {
    try {
      await axios.put(`${serverURL}/itemDomain/putItemUbicacion`, {
        idItem,
        idUbicacion,
        inventario: inventarioItem,
        proviene,
      })
    } catch (error) {
      console.error("Error updating item ubicacion:", error)
    }
  }

  const getImpuestoItem = async (idItem) => {
    try {
      const response = await axios.get(`${serverURL}/itemDomain/getImpuestoItem/${idItem}`)
      return response.data
    } catch (error) {
      console.error("Error fetching impuesto item:", error)
      return {
        idItem: parseInt(idItem),
        idImpuesto: null,
        porcentaje: 21,
        descripcionImpuesto: "IVA General",
      }
    }
  }

  const updateImpuestoItem = async (idItem, idImpuesto, porcentaje) => {
    try {
      const response = await axios.put(`${serverURL}/itemDomain/updateImpuestoItem/${idItem}`, {
        idImpuesto,
        porcentaje,
      })
      return response.data
    } catch (error) {
      console.error("Error updating impuesto item:", error)
      throw error
    }
  }

  const getLoteMasAntiguoConStock = async (idItem, idUbicacion) => {
    try {
      const response = await axios.get(`${serverURL}/itemDomain/lotes/fifo/${idItem}/${idUbicacion}`)
      return response.data
    } catch (error) {
      console.error("Error fetching lote FIFO:", error)
      return null
    }
  }

  const descontarStockVenta = async (lotesVendidos, idUbicacion) => {
    try {
      const response = await axios.post(`${serverURL}/itemDomain/lotes/descontar-venta`, {
        lotesVendidos,
        idUbicacion,
      })
      return response.data
    } catch (error) {
      console.error("Error descontando stock de venta:", error)
      return null
    }
  }

  const getSugerenciaLotesVenta = async (idItem, idUbicacion, cantidadNecesaria) => {
    try {
      const response = await axios.get(`${serverURL}/itemDomain/lotes/sugerencia-venta`, {
        params: { idItem, idUbicacion, cantidadNecesaria },
      })
      return response.data
    } catch (error) {
      console.error("Error fetching sugerencia de lotes:", error)
      return { lotesSugeridos: [], cantidadCubierta: 0, stockDisponible: 0, stockSuficiente: false }
    }
  }

  const getLotesDisponiblesVenta = async (idItem, idUbicacion) => {
    try {
      const response = await axios.get(`${serverURL}/itemDomain/lotes/disponibles-venta`, {
        params: { idItem, idUbicacion },
      })
      return response.data
    } catch (error) {
      console.error("Error fetching lotes disponibles:", error)
      return []
    }
  }

  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // API: ENTIDAD (entidadController en su dominio)
  const getEntidadesClientesTresLetras = async (searchTextCliente, tipoEntidad, verEnCaja = false, verEnGasto = false) => {
    const search = String(searchTextCliente ?? "").trim()
    if (search.length < 3) {
      setEntidadesClientesTresLetras([])
      return []
    }

    try {
      const params = {
        search,
        tipoEntidad,
      }
      if (verEnCaja === true) params.verEnCaja = "true"
      if (verEnGasto === true) params.verEnGasto = "true"

      const response = await axios.get(`${serverURL}/adminDomain/getEntidadClienteTresLetras`, { params })
      setEntidadesClientesTresLetras(response.data)
      return response.data
    } catch (error) {
      if (error?.response?.status === 400) {
        setEntidadesClientesTresLetras([])
        return []
      }
      console.error("Error fetching entidades:", error)
      return []
    }
  }

  const getEntidadByID = async (idEntidad) => {
    try {
      const response = await axios.get(`${serverURL}/adminDomain/getEntidadByID/${idEntidad}`)
      return response.data
    } catch (error) {
      console.error("Error fetching entidades:", error)
    }
  }

  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // API: ENTIDAD / ADMIN (ubicacion, negocio)
  const getUbicacion = async () => {
    try {
      const response = await axios.get(`${serverURL}/adminDomain/getUbicacion`)
      setUbicaciones(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching ubicaciones:", error)
    }
  }

  const getNegocio = async () => {
    try {
      const response = await axios.get(`${serverURL}/adminDomain/getNegocio`)
      setNegocios(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching negocios:", error)
    }
  }

  const getTipoEntidad = async () => {
    try {
      const response = await axios.get(`${serverURL}/adminDomain/getTipoEntidad`)
      setTipoEntidades(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching tipo entidad:", error)
      return []
    }
  }

  const getUsuario = async () => {
    try {
      const response = await axios.get(`${serverURL}/adminDomain/getUsuario`)
      setUsuarios(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching usuarios:", error)
      return []
    }
  }

  const getParametrosGlobales = async (parametros) => {
    try {
      const response = await axios.post(`${serverURL}/adminDomain/getParametrosGlobales`, {
        parametros,
      })
      return response.data
    } catch (error) {
      console.error("Error fetching parametros globales:", error)
      return null
    }
  }

  const getIncluirImpuestos = async () => {
    try {
      const response = await axios.get(`${serverURL}/adminDomain/getIncluirImpuestos`)
      return response.data
    } catch (error) {
      console.error("Error fetching incluir impuestos:", error)
      return null
    }
  }

  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // API: TRANSACCION (tabla de lista precios - transaccionController)
  const getListaMontosByIdItemIdEntidad = async (idEntidad, idItem, idUbicacion) => {
    try {
      const response = await axios.get(
        `${serverURL}/transaccionDomain/getListaMontosByIdItemIdEntidad`,
        {
          params: { idEntidad, idItem, idUbicacion },
        },
      )
      return response.data
    } catch (error) {
      console.error("Error fetching lista montos:", error)
    }
  }

  const getTop10ItemVentas = async () => {
    try {
      const response = await axios.get(`${serverURL}/transaccionDomain/getTop10ItemVentas`)
      return response.data
    } catch (error) {
      console.error("Error al pedir 10 items mas vendidos:", error)
    }
  }

  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // API: TRANSACCION (ROUTES GENERALES)
  const postTransaccion = async (
    montoTotal,
    idTipoTransaccion,
    idEntidad,
    idSubCategoriaTransaccion,
    descripcion,
    idUsuario,
    idUbicacion,
    idNegocio,
    montoDescuento,
    porcentajeDescuento,
    transaccionAsociada = null,
    fecha = null,
    afectaCuentaCorriente = false,
    clientOS = null,
    operacionParaCuentaCorriente = null,
    archivosAdjuntos = null,
  ) => {
    try {
      const fechaTransaccion = fecha ? fecha : new Date()
      const response = await axios.post(`${serverURL}/transaccionDomain/postTransaccion`, {
        fecha: fechaTransaccion,
        montoTotal,
        idTipoTransaccion,
        idEntidad,
        idCategorizacion: idSubCategoriaTransaccion,
        descripcion,
        fechaHoraCreacion: fechaTransaccion,
        idUsuario,
        idUbicacion,
        idNegocio,
        transaccionAsociada,
        montoDescuento,
        porcentajeDescuento,
        afectaCuentaCorriente,
        operacionParaCuentaCorriente,
        clientOS,
        archivosAdjuntos: archivosAdjuntos ? JSON.stringify(archivosAdjuntos) : null,
      })
      return response
    } catch (error) {
      console.error("Error creating transaccion:", error)
    }
  }

  const postTransaccionItem = async (
    idTransaccion,
    idItem,
    cantidad,
    precio,
    porcentajeDescuento,
    lotes,
    idUbicacion,
  ) => {
    try {
      const response = await axios.post(`${serverURL}/transaccionDomain/postTransaccionItem`, {
        idTransaccion,
        idItem,
        cantidad,
        precio,
        montoTotal: precio * cantidad * (1 - (porcentajeDescuento || 0) / 100),
        porcentajeDescuento,
        lotes,
        idUbicacion,
      })
      return response
    } catch (error) {
      console.error("Error creating transaccion item:", error)
    }
  }

  const putTransaccion = async (
    idTransaccion,
    montoTotal,
    idTipoTransaccion,
    idEntidad,
    idCategorizacion,
    descripcion,
    idUsuario,
    idUbicacion,
    idNegocio,
    montoDescuento,
    porcentajeDescuento,
    transaccionAsociada = null,
    fecha = null,
  ) => {
    try {
      const requestBody = {
        montoTotal,
        idTipoTransaccion,
        idEntidad,
        idCategorizacion,
        descripcion,
        idUsuario,
        idUbicacion,
        idNegocio,
        montoDescuento,
        porcentajeDescuento,
        transaccionAsociada,
      }
      if (fecha) {
        requestBody.fecha = fecha
        requestBody.fechaHoraCreacion = fecha
      }
      const response = await axios.put(
        `${serverURL}/transaccionDomain/putTransaccion/${idTransaccion}`,
        requestBody,
      )
      return response
    } catch (error) {
      console.error("Error updating transaccion:", error)
    }
  }

  const getTransaccionFilter = async (idTransaccion) => {
    try {
      const response = await axios.get(
        `${serverURL}/transaccionDomain/getTransaccionFilter/${idTransaccion}`,
      )
      return response.data
    } catch (error) {
      console.error("Error fetching transaccion filter:", error)
      return null
    }
  }

  const getTransaccionItemsByTransactionId = async (idTransaccion) => {
    try {
      const response = await axios.get(
        `${serverURL}/transaccionDomain/transaccionItemsById/${idTransaccion}`,
      )
      return response.data
    } catch (error) {
      if (error.response && error.response.status === 404) return []
      console.error("Error fetching transaccion items:", error)
      return []
    }
  }

  // #########################################################################################################################
  // PREVENTAS (dominio transaccion)
  // #########################################################################################################################

  const getPreventas = async (params = {}) => {
    try {
      const { valorMin, valorMax, ...safeParams } = params || {}
      const response = await axios.get(`${serverURL}/transaccionDomain/preventas/list`, {
        params: safeParams,
      })
      return response.data
    } catch (error) {
      console.error("Error fetching preventas:", error)
      return { data: [], totalCount: 0, totalPages: 0, page: 1, limit: 50 }
    }
  }

  const getPreventaDetail = async (idTransaccion, incluirEliminados = false) => {
    try {
      const response = await axios.get(
        `${serverURL}/transaccionDomain/preventas/${idTransaccion}/detail`,
        {
          params: { incluirEliminados },
        },
      )
      return response.data
    } catch (error) {
      console.error("Error fetching preventa detail:", error)
      return null
    }
  }

  const convertirPreventaAVenta = async (idTransaccion, payload = {}) => {
    try {
      const response = await axios.put(
        `${serverURL}/transaccionDomain/preventas/${idTransaccion}/convertirAVenta`,
        payload,
      )
      return response.data
    } catch (error) {
      console.error("Error converting preventa to venta:", error)
      return null
    }
  }

  const deletePreventa = async (idTransaccion) => {
    try {
      const response = await axios.delete(`${serverURL}/transaccionDomain/preventas/${idTransaccion}`)
      return response.data
    } catch (error) {
      console.error("Error deleting preventa:", error)
      throw error
    }
  }

  // #########################################################################################################################
  // PEDIDOS (dominio transaccion)
  // #########################################################################################################################

  const getPedidos = async (params = {}) => {
    try {
      const response = await axios.get(`${serverURL}/transaccionDomain/pedidos/list`, {
        params,
      })
      return response.data
    } catch (error) {
      console.error("Error fetching pedidos:", error)
      return { data: [], totalCount: 0, totalPages: 0, page: 1, limit: 50 }
    }
  }

  const getPedidoDetail = async (id, incluirEliminados = false) => {
    try {
      const response = await axios.get(
        `${serverURL}/transaccionDomain/pedidos/${id}/detail`,
        { params: { incluirEliminados } },
      )
      return response.data
    } catch (error) {
      console.error("Error fetching pedido detail:", error)
      return null
    }
  }

  const cambiarEstadoPedido = async (id, estado, idMedioDePago = null) => {
    try {
      const payload = { estado }
      if (idMedioDePago) payload.idMedioDePago = idMedioDePago
      const response = await axios.put(
        `${serverURL}/transaccionDomain/pedidos/${id}/estado`,
        payload,
      )
      return { ok: true, data: response.data }
    } catch (error) {
      const message = error?.response?.data?.message || "Error al cambiar estado del pedido"
      console.error("Error cambiando estado de pedido:", message)
      return { ok: false, message }
    }
  }

  const deleteTransaccionItem = async (idTransaccion, idItem) => {
    try {
      const response = await axios.put(
        `${serverURL}/transaccionDomain/deleteTransaccionItem/${idTransaccion}/${idItem}`,
      )
      return response.data
    } catch (error) {
      console.error("Error deleting transaccion item:", error)
      return null
    }
  }

  const deleteTransaccionPago = async (idTransaccion) => {
    try {
      const response = await axios.put(
        `${serverURL}/transaccionDomain/deleteTransaccionPago/${idTransaccion}`,
      )
      return response.data
    } catch (error) {
      console.error("Error deleting transaccion pago:", error)
      return null
    }
  }

  const updateTransaccionItem = async (idTransaccion, idItem, data) => {
    try {
      const response = await axios.put(
        `${serverURL}/transaccionDomain/actualizarTransaccionExistente/${idTransaccion}/${idItem}`,
        data,
      )
      return response.data
    } catch (error) {
      console.error("Error updating transaccion item:", error)
      return null
    }
  }

  const postCrearPago = async (
    idMoneda,
    cotizacion,
    idMedioDePago,
    montoTotal,
    fechaOriginal = null,
  ) => {
    try {
      const response = await axios.post(`${serverURL}/transaccionDomain/postCrearPago`, {
        idMoneda,
        cotizacion,
        idMedioDePago,
        montoTotal,
        ...(fechaOriginal && { fechaOriginal }),
      })
      return response
    } catch (error) {
      console.error("Error creating pago:", error)
    }
  }

  const postTransaccionPago = async (idTransaccion, idPago, fechaOriginal = null) => {
    try {
      const response = await axios.post(`${serverURL}/transaccionDomain/postTransaccionPago`, {
        idTransaccion,
        idPago,
        ...(fechaOriginal && { fechaOriginal }),
      })
      return response
    } catch (error) {
      console.error("Error creating transaccion pago:", error)
    }
  }

  const postTransaccionImpuesto = async (idTransaccion, idImpuesto, porcentaje, montoTotal) => {
    try {
      const response = await axios.post(`${serverURL}/transaccionDomain/postTransaccionImpuesto`, {
        idTransaccion,
        idImpuesto,
        porcentaje,
        montoTotal,
      })
      return response
    } catch (error) {
      console.error("Error creating transaccion impuesto:", error)
      return null
    }
  }

  const postTransaccionImpuestoItem = async (
    idTransaccion,
    idItem,
    idImpuesto,
    porcentaje,
    montoTotal,
  ) => {
    try {
      const response = await axios.post(
        `${serverURL}/transaccionDomain/postTransaccionImpuestoItem`,
        {
          idTransaccion,
          idItem,
          idImpuesto,
          porcentaje,
          montoTotal,
        },
      )
      return response
    } catch (error) {
      console.error("Error creating transaccion impuesto item:", error)
      return null
    }
  }

  const updateTransaccionImpuestoItem = async (
    idTransaccion,
    idItem,
    idImpuesto,
    porcentaje,
    montoTotal,
  ) => {
    try {
      const response = await axios.put(
        `${serverURL}/transaccionDomain/updateTransaccionImpuestoItem/${idTransaccion}/${idItem}`,
        { idImpuesto, porcentaje, montoTotal },
      )
      return response.data
    } catch (error) {
      console.error("Error updating transaccion impuesto item:", error)
      return null
    }
  }

  const postTransaccionTipoFactura = async (
    idTipoFactura,
    idTransaccion,
    CAE,
    vencimientoCAE,
    idCondicionIva,
    numeroFactura,
  ) => {
    try {
      const response = await axios.post(`${serverURL}/transaccionDomain/ventas/postTransaccionTipoFactura`, {
        idTipoFactura,
        idTransaccion,
        CAE,
        vencimientoCAE,
        idCondicionIva,
        numeroFactura,
      })
      return response
    } catch (error) {
      console.error("Error creating transaccion tipo factura:", error)
    }
  }

  const getTipoMedioDePago = async (condicionExtra) => {
    try {
      const response = await axios.get(`${serverURL}/transaccionDomain/getTipoMedioDePago`, {
        params: { condicionExtra },
      })
      setTipoMedioDePago(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching tipo medio de pago:", error)
      return []
    }
  }

  const getMedioDePagoIdTipoEspecifico = async (selectedTipoMedioDePago) => {
    try {
      const id =
        typeof selectedTipoMedioDePago === "object"
          ? selectedTipoMedioDePago?.id
          : selectedTipoMedioDePago
      if (!id) return []
      const response = await axios.get(
        `${serverURL}/transaccionDomain/getMedioDePagoConDescripcion/${id}`,
      )
      setMedioDePagoIdTipoEspecifico(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching medios de pago por tipo:", error)
      return []
    }
  }

  const getCuentaCorrienteByIdEntidad = async (idEntidad) => {
    try {
      if (!idEntidad || isNaN(Number(idEntidad))) return []
      const response = await axios.get(
        `${serverURL}/transaccionDomain/getCuentaCorrienteByIdEntidad/${idEntidad}`,
      )
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      console.error("Error fetching cuenta corriente:", error)
      return []
    }
  }

  const postCuentaCorriente = async (idEntidad, monto, idTipoTransaccion) => {
    try {
      const response = await axios.post(`${serverURL}/transaccionDomain/cuentaCorriente`, {
        idEntidad,
        monto,
        idTipoTransaccion,
      })
      return response.data
    } catch (error) {
      console.error("Error creating cuenta corriente:", error)
      return null
    }
  }

  const updateCuentaCorriente = async (
    idEntidad,
    monto,
    idTipoTransaccion,
    usarOperacionCaja = false,
  ) => {
    try {
      const response = await axios.put(`${serverURL}/transaccionDomain/cuentaCorriente/${idEntidad}`, {
        monto,
        idTipoTransaccion,
        usarOperacionCaja,
      })
      return response.data
    } catch (error) {
      console.error("Error updating cuenta corriente:", error)
      return null
    }
  }

  const getImpuestos = async () => {
    try {
      const response = await axios.get(`${serverURL}/transaccionDomain/getImpuestos`)
      setImpuestos(response.data)
      setArrayImpuestos(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching impuestos:", error)
      return []
    }
  }

  const getTiposFacturas = async () => {
    try {
      const response = await axios.get(`${serverURL}/transaccionDomain/getTiposFacturas`)
      setTiposFacturas(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching tipos facturas:", error)
      return []
    }
  }

  const getCondicionIvaByIdEntidad = async (idEntidad) => {
    try {
      const response = await axios.get(
        `${serverURL}/transaccionDomain/ventas/getCondicionIvaByIdEntidad/${idEntidad}`,
      )
      return response.data
    } catch (error) {
      console.error("Error fetching condicion iva:", error)
      return null
    }
  }

  const updateItemPrecioListaId = async (
    idEntidad,
    idItem,
    idUbicacion,
    monto,
    numeroLista,
    idTipoMoneda = 1,
    cotizacion = 1,
    fecha = null,
    datosAdicionales = {},
  ) => {
    try {
      const payload = {
        idEntidad,
        idItem,
        idUbicacion,
        monto,
        numeroLista,
        idTipoMoneda,
        cotizacion,
        fecha: fecha || new Date().toISOString(),
        ...datosAdicionales,
      }
      const response = await axios.post(`${serverURL}/transaccionDomain/updateItemPrecioListaId`, payload)
      return response.data
    } catch (error) {
      console.error("Error updating item precio lista:", error)
      return null
    }
  }

  const revertirLotesDeTransaccion = async (idTransaccion) => {
    try {
      const response = await axios.put(
        `${serverURL}/transaccionDomain/ventas/revertirLotesDeTransaccion/${idTransaccion}`,
      )
      return response.data
    } catch (error) {
      console.error("Error reverting lotes de transaccion:", error)
      return null
    }
  }

  const getLotesDeTransaccion = async (idTransaccion) => {
    try {
      const response = await axios.get(`${serverURL}/transaccionDomain/ventas/lotesDeTransaccion/${idTransaccion}`)
      return response.data
    } catch (error) {
      if (error.response?.status === 404) return []
      console.error("Error fetching lotes de transaccion:", error)
      return []
    }
  }

  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // #########################################################################################################################
  // Helpers compartidos para compatibilidad con componentes existentes
  const ivaHardcoded = 21
  const getIvaAFIP = () => {
    return [
      { id: 1, idIva: 1, descripcion: "IVA 0%", porcentaje: 0, ivaAFIP: 3 },
      { id: 2, idIva: 1, descripcion: "IVA 10.5%", porcentaje: 10.5, ivaAFIP: 4 },
      { id: 3, idIva: 1, descripcion: "IVA 21%", porcentaje: 21, ivaAFIP: 5 },
      { id: 4, idIva: 1, descripcion: "IVA 27%", porcentaje: 27, ivaAFIP: 6 },
    ]
  }

  const getFechaHoraActualClient = (fechahora = null) => {
    const zona = Intl.DateTimeFormat().resolvedOptions().timeZone
    const fecha = fechahora ? dayjs(fechahora).tz(zona) : dayjs().tz(zona)
    return {
      fechaServidor: fecha.format(),
      zona,
    }
  }

  const enviarFacturaAfip = async (payload) => {
    const res = await axios.post(`${serverURL}/api/afip/invoice`, payload)
    const data = await res.data

    await postTransaccionTipoFactura(
      payload.idTipoFacturaTransaccion,
      payload.idTransaccion,
      data.cae,
      data.vto,
      payload.idCondicionIva,
      data.cbteNro,
    )

    if (payload.abrirPdf === true) {
      try {
        await abrirFacturaHtml(data, payload, getParametrosGlobales)
      } catch (error) {
        console.error("Error al generar PDF de factura:", error)
      }
    }

    return data
  }

  return (
    <ApiContextDominios.Provider
      value={{
        serverURL,
        // item/entidad
        itemTresLetras,
        arrayItemTresLetras: setItemTresLetras,
        getItemTresLetras,
        clearItemTresLetras,
        getItemById,
        getItemUbicacionFilterByUbicacion,
        getItemAtributoNoEliminados,
        itemAtributoNoEliminados,
        updateItemUbicacion,
        getLoteMasAntiguoConStock,
        descontarStockVenta,
        getSugerenciaLotesVenta,
        getLotesDisponiblesVenta,
        getImpuestoItem,
        updateImpuestoItem,
        entidadesClientesTresLetras,
        getEntidadesClientesTresLetras,
        getEntidadByID,
        ubicaciones,
        getUbicacion,
        negocios,
        getNegocio,
        tipoEntidades,
        getTipoEntidad,
        usuarios,
        getUsuario,
        getParametrosGlobales,
        getIncluirImpuestos,
        getListaMontosByIdItemIdEntidad,
        getTop10ItemVentas,
        // transaccion generales
        postTransaccion,
        postTransaccionItem,
        putTransaccion,
        getTransaccionFilter,
        getTransaccionItemsByTransactionId,
        getPreventas,
        getPreventaDetail,
        getPedidos,
        getPedidoDetail,
        cambiarEstadoPedido,
        convertirPreventaAVenta,
        deletePreventa,
        deleteTransaccionItem,
        deleteTransaccionPago,
        updateTransaccionItem,
        postCrearPago,
        postTransaccionPago,
        postTransaccionImpuesto,
        postTransaccionImpuestoItem,
        updateTransaccionImpuestoItem,
        postTransaccionTipoFactura,
        getTipoMedioDePago,
        tipoMedioDePago,
        getMedioDePagoIdTipoEspecifico,
        medioDePagoIdTipoEspecifico,
        getCuentaCorrienteByIdEntidad,
        postCuentaCorriente,
        updateCuentaCorriente,
        cuentasCorrientes,
        getImpuestos,
        impuestos,
        arrayImpuestos,
        getTiposFacturas,
        tiposFacturas,
        getCondicionIvaByIdEntidad,
        updateItemPrecioListaId,
        getLotesDeTransaccion,
        revertirLotesDeTransaccion,
        ivaHardcoded,
        getIvaAFIP,
        getFechaHoraActualClient,
        enviarFacturaAfip,
        // VENTAS / PREVENTA (dominio)
        // Definir en este sector las funciones especificas por dominio
      }}
    >
      {children}
    </ApiContextDominios.Provider>
  )
}
